import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useIsAuthenticated } from '@azure/msal-react'
import { useEffect } from 'react'
import { AuthenticatedLayout } from '@/layouts/AuthenticatedLayout'
import { LoginPage } from '@/views/LoginPage'
import { CheckoutSuccess } from '@/views/CheckoutSuccess'
import { PlaybookPage } from '@/views/PlaybookPage'
import { ThanksPage } from '@/views/ThanksPage'
import { PrivacyPolicy } from '@/views/PrivacyPolicy'
import { TermsOfService } from '@/views/TermsOfService'
import { DataDeletion } from '@/views/DataDeletion'
import { Changelog } from '@/views/Changelog'
import { CommandCenter } from '@/views/CommandCenter'
import { FacebookAds } from '@/views/FacebookAds'
import { Email } from '@/views/Email'
import { Metrics } from '@/views/Metrics'
import { ActivityLog } from '@/views/ActivityLog'
import { Settings } from '@/views/Settings'
import { Billing } from '@/views/Billing'
import { Setup } from '@/views/Setup'
import { Guide } from '@/views/Guide'
import { Profile } from '@/views/Profile'
import Landing from '@/pages/Landing'
import { apiClient } from '@/lib/apiClient'
import { HAS_AUTH } from '@/lib/auth'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useIsAuthenticated()
  if (!HAS_AUTH) return <>{children}</>
  return isAuthenticated ? <>{children}</> : <Navigate to="/" replace />
}

function ProvisionOnLogin() {
  const isAuthenticated = useIsAuthenticated()
  useEffect(() => {
    if (HAS_AUTH && isAuthenticated) {
      apiClient.post('/users/provision').catch(() => undefined)
    }
  }, [isAuthenticated])
  return null
}

export default function App() {
  return (
    <BrowserRouter>
      <ProvisionOnLogin />
      <Routes>
        {/* Public landing — no auth. Redirects authed users into the app. */}
        <Route path="/" element={<Landing />} />
        <Route path="/landing" element={<Navigate to="/" replace />} />

        {/* Public pages */}
        <Route path="/login" element={<LoginPage />} />
        {/* Stripe Checkout `success_url` lands here. Confirms purchase + triggers sign-in. */}
        <Route path="/auth/callback" element={<CheckoutSuccess />} />
        <Route path="/playbook" element={<PlaybookPage />} />
        <Route path="/thanks" element={<ThanksPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/data-deletion" element={<DataDeletion />} />
        <Route path="/changelog" element={<Changelog />} />

        {/* Protected app — everything sits behind /dashboard but each route
            still has its own short top-level alias for back-compat. */}
        <Route
          element={
            <ProtectedRoute>
              <AuthenticatedLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<CommandCenter />} />
          <Route path="/ads" element={<FacebookAds />} />
          <Route path="/email" element={<Email />} />
          <Route path="/metrics" element={<Metrics />} />
          <Route path="/activity" element={<ActivityLog />} />
          <Route path="/billing" element={<Billing />} />
          <Route path="/setup" element={<Setup />} />
          <Route path="/guide" element={<Guide />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/profile" element={<Profile />} />
        </Route>
      </Routes>
    </BrowserRouter>
  )
}
